import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Card, CardBody, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, Tag } from "react-feather";

const AddEdit_VehicleCategoryMaster = () => {
  const API_URL_SAVE = "VehicleCategory/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/VehicleCategory/Id";

  const [state, setState] = useState({
    id: 0,
    formData: {
      Name: "",
      Alias: "",
      PhotoIcon: "",
    },
    photoPreview: null,
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const nameRef = useRef(null);

  useEffect(() => {
    const Id = (location.state && location.state.Id) || 0;
    if (Id > 0) {
      setState((prevState) => ({ ...prevState, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    }
  }, [dispatch, location.state]);

  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);

  const validationSchema = Yup.object({
    Name: Yup.string()
      .required("Category name is required")
      .min(2, "Category name must be at least 2 characters")
      .max(100, "Category name must be less than 100 characters"),
    Alias: Yup.string()
      .required("Alias is required")
      .max(50, "Alias must be less than 50 characters"),
    PhotoIcon: Yup.mixed()
      .required("Photo icon is required"),
  });

  const handleSubmit = (values) => {
    const obj = JSON.parse(localStorage.getItem("user"));
    let formData = new FormData();
    formData.append("Name", values.Name || "");
    formData.append("Alias", values.Alias || "");
    if (values.PhotoIcon && values.PhotoIcon instanceof File) {
      formData.append("PhotoIcon", values.PhotoIcon);
    }
    formData.append("UserId", obj === null || obj === undefined ? 0 : obj.uid);

    Fn_AddEditData(
      dispatch,
      setState,
      { arguList: { id: state.id, formData } },
      API_URL_SAVE,
      true,
      "vehiclecategoryid",
      navigate,
      "/VehicleCategoryMaster"
    );
  };

  return (
    <div style={{ padding: "6px 8px", maxWidth: 900, margin: "0 auto" }}>
      <Card className="border shadow-none" style={{ marginBottom: 0 }}>
        <CardBody className="p-2" style={{ padding: "8px 12px !important" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid #dee2e6" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>Vehicle Category Master</span>
            <div style={{ display: "flex", gap: 6 }}>
              <Button type="button" size="sm" color="light" className="py-1 px-2" style={{ fontSize: 12 }} onClick={() => navigate("/VehicleCategoryMaster")}>
                <ArrowLeft size={12} className="me-1" />Back
              </Button>
              <Button type="submit" form="vehiclecategory-form" size="sm" color="primary" className="py-1 px-2" style={{ fontSize: 12 }}>
                <Save size={12} className="me-1" />Save
              </Button>
            </div>
          </div>

          <Formik
            initialValues={{
              Name: state.formData?.Name ?? "",
              Alias: state.formData?.Alias ?? "",
              PhotoIcon: state.formData?.PhotoIcon ?? "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ errors, touched }) => (
              <Form id="vehiclecategory-form">
                <div className="form-group mb-3">
                  <label htmlFor="Name" className="master-form-label">
                    <Tag size={16} className="me-2" />
                    Category Name <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <Field
                      innerRef={nameRef}
                      className={`master-form-input ${errors.Name && touched.Name ? "is-invalid" : ""}`}
                      type="text"
                      name="Name"
                      placeholder="Enter vehicle category name (e.g., Truck, 2 Wheeler, 4 Wheeler)"
                    />
                    <div className="input-icon">
                      <Tag size={16} color="#6c757d" />
                    </div>
                  </div>
                  <ErrorMessage name="Name" component="div" className="master-form-error" />
                  <div className="form-help-text" style={{ fontSize: "0.75rem", marginTop: "2px" }}>
                    <i className="fas fa-lightbulb me-1"></i>
                    Enter the category of vehicle (e.g., Truck, 2 Wheeler, 4 Wheeler, Auto)
                  </div>
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="Alias" className="master-form-label">
                    <Tag size={16} className="me-2" />
                    Alias <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <Field
                      className={`master-form-input ${errors.Alias && touched.Alias ? "is-invalid" : ""}`}
                      type="text"
                      name="Alias"
                      placeholder="Enter alias / short name (e.g., TRK, 2W, 4W)"
                    />
                    <div className="input-icon">
                      <Tag size={16} color="#6c757d" />
                    </div>
                  </div>
                  <ErrorMessage name="Alias" component="div" className="master-form-error" />
                  <div className="form-help-text" style={{ fontSize: "0.75rem", marginTop: "2px" }}>
                    <i className="fas fa-lightbulb me-1"></i>
                    Enter a short alias or abbreviation for the category
                  </div>
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="PhotoIcon" className="master-form-label">
                    Photo Icon <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <Field name="PhotoIcon">
                      {({ field, form }) => (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            className={`master-form-input ${errors.PhotoIcon && touched.PhotoIcon ? "is-invalid" : ""}`}
                            onChange={e => {
                              const file = e.target.files[0];
                              form.setFieldValue("PhotoIcon", file);
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = ev => setState(prev => ({ ...prev, photoPreview: ev.target.result }));
                                reader.readAsDataURL(file);
                              } else {
                                setState(prev => ({ ...prev, photoPreview: null }));
                              }
                            }}
                          />
                          {/* Show preview for new upload */}
                          {state.photoPreview && (
                            <div style={{ marginTop: 8 }}>
                              <img src={state.photoPreview} alt="Preview" style={{ maxWidth: 120, maxHeight: 80, borderRadius: 4, border: "1px solid #ccc" }} />
                            </div>
                          )}
                          {/* Show old image if editing and no new preview */}
                          {!state.photoPreview && state.formData.PhotoIcon && typeof state.formData.PhotoIcon === "string" && (
                            <div style={{ marginTop: 8 }}>
                              <img src={API_WEB_URLS.IMAGEURL + state.formData.PhotoIcon} alt="Old" style={{ maxWidth: 120, maxHeight: 80, borderRadius: 4, border: "1px solid #ccc" }} />
                            </div>
                          )}
                        </>
                      )}
                    </Field>
                  </div>
                  <ErrorMessage name="PhotoIcon" component="div" className="master-form-error" />
                  <div className="form-help-text" style={{ fontSize: "0.75rem", marginTop: "2px" }}>
                    <i className="fas fa-lightbulb me-1"></i>
                    Upload an image for the vehicle category icon.
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </CardBody>
      </Card>
    </div>
  );
};

export default AddEdit_VehicleCategoryMaster;
